export type AppErrorKind = "chunk-load" | "generic";
export type AppErrorLocale = "de" | "en";

const CHUNK_LOAD_PATTERNS = [
  /Loading chunk \d+ failed/i,
  /ChunkLoadError/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /CSS chunk \d+ failed/i,
  /Failed to find Server Action/i,
  /Invalid Server Actions request/i,
];

export function normalizeErrorLocale(locale?: string | null): AppErrorLocale {
  return locale?.toLowerCase().startsWith("en") ? "en" : "de";
}

export function isChunkLoadError(error?: Error | null): boolean {
  if (!error) return false;

  const searchable = [error.name, error.message].filter(Boolean).join(" ");
  return CHUNK_LOAD_PATTERNS.some((pattern) => pattern.test(searchable));
}

export function classifyAppError(error?: Error | null): AppErrorKind {
  return isChunkLoadError(error) ? "chunk-load" : "generic";
}

export function getAppErrorCopy(error?: Error | null, locale?: string | null) {
  const normalizedLocale = normalizeErrorLocale(locale);
  const kind = classifyAppError(error);

  const copies = {
    de: {
      "chunk-load": {
        eyebrow: "Aktualisierung erforderlich",
        title: "Die Seite wurde aktualisiert",
        description:
          "Diese Ansicht konnte nicht geladen werden, weil im Hintergrund eine neue Version des Portals bereitgestellt wurde. Laden Sie die Seite neu, dann können Sie weiterarbeiten.",
        primaryAction: "Seite neu laden",
        secondaryAction: "Zum Kundenportal",
        homeAction: "Zur Startseite",
      },
      generic: {
        eyebrow: "Unerwarteter Fehler",
        title: "Etwas ist schiefgelaufen",
        description:
          "Bitte versuchen Sie es erneut. Wenn der Fehler bestehen bleibt, laden Sie die Seite neu oder öffnen Sie das Kundenportal erneut.",
        primaryAction: "Erneut versuchen",
        secondaryAction: "Zum Kundenportal",
        homeAction: "Zur Startseite",
      },
    },
    en: {
      "chunk-load": {
        eyebrow: "Update required",
        title: "This page was updated",
        description:
          "This view could not be loaded because a new portal version was deployed while this tab was open. Reload the page to continue.",
        primaryAction: "Reload page",
        secondaryAction: "Go to customer portal",
        homeAction: "Go to homepage",
      },
      generic: {
        eyebrow: "Unexpected error",
        title: "Something went wrong",
        description:
          "Please try again. If the error remains, reload the page or open the customer portal again.",
        primaryAction: "Try again",
        secondaryAction: "Go to customer portal",
        homeAction: "Go to homepage",
      },
    },
  } satisfies Record<
    AppErrorLocale,
    Record<
      AppErrorKind,
      {
        eyebrow: string;
        title: string;
        description: string;
        primaryAction: string;
        secondaryAction: string;
        homeAction: string;
      }
    >
  >;

  return { kind, locale: normalizedLocale, ...copies[normalizedLocale][kind] };
}
